/* tslint:disable:template-cyclomatic-complexity max-file-line-count */
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Inject, Input, OnInit, Output, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { DataDictionaryApi } from '../../api/data-dictionary-api';
import { GisToolsApi } from '../../api/gis-tools-api';
import { ESRI } from '../../esri-loader-util';
import { BooleanConverter, InputConverter } from '../../inputConverter';
import { keys } from '../../keys';
import { DocumentService } from '../../services/document.service';
import { IAuthenticationService } from '../../services/interfaces/IAuthenticationService';
import { ILoggingService } from '../../services/interfaces/ILoggingService';
import { IUserSettingsService } from '../../services/interfaces/IUserSettingsService';
import { LayerService } from '../../services/layer.service';
import { StorageService } from '../../services/storage.service';
import { ToSqlStringService } from '../../services/to-sql-string.service';
import { ColumnSortOrder, IAttribute, IColumnSettingsStorage, IDocumentSource, ILayerColumn, ILayerTableChild, IMapcoreFeatureLayer, IMapcoreProps, IMapcoreSubLayer, IMetaTableColumn, IQuery, IRMS, ISmallColumnSetting, isPointGeometry, LogLevel } from '../../types';
import { BaseComponent } from '../base.component';

const urlMaxLength = 2048;

let Graphic: __esri.GraphicConstructor;

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-identify',
    templateUrl: './identify.component.html',
    styleUrls: ['../sharedStyles/base.scss', './identify.component.scss']
})
export class IdentifyComponent extends BaseComponent implements OnInit, AfterViewInit {
    private readonly DEFAULT_TOLERANCE = 10000;

    mapView: __esri.MapView;
    loading: boolean;

    @Input()
    @InputConverter(BooleanConverter)
    get showIdentify() {
        return this._showIdentify;
    }
    set showIdentify(value) {
        this._showIdentify = value;
        if (!value) {
            this.clearIdentifyHighlightLayer(!this.isMultiselectClick);
            this.clearIdentifyHighlightLayer(this.isMultiselectClick);
        }
    }
    private _showIdentify: boolean = false;

    @Input()
    @InputConverter(BooleanConverter)
    showNearbyAssets: boolean = false;

    @Input()
    @InputConverter(BooleanConverter)
    showNearbyAssetsBtn: boolean = false;

    @Input()
    @InputConverter(BooleanConverter)
    enableNearbyMapMode: boolean = false;

    @Input()
    @InputConverter(BooleanConverter)
    showShowAll: boolean = true;

    @Output() readonly nearbyActiveChange: EventEmitter<boolean> = new EventEmitter();

    @Input()
    @InputConverter(BooleanConverter)
    enableBuffer: boolean = true;

    @Input()
    currentFeature: IFeature;
    currentFeatureIndex: number;
    @Input()
    highlightColor: string = 'rgba(125, 247, 229, 0.75)';
    @Input()
    multiselectHighlightColor: string = 'rgba(250, 242, 10, 0.75)';
    @Input()
    highlightMarkerSize: number = 12;
    @Input()
    highlightLineWidth: number = 3;

    @Input()
    public reset = () => {
        this.resetIdentify();
    }

    identifyHandler;

    loggedIn = false;
    isCwopaUser = false;
    showAdditionalInformation: boolean = true;

    private features: IFeature[];
    private selectedFeatures: IFeature[];
    private identifyHighlightLayer: __esri.GraphicsLayer;
    private multiIdentifyHighlightLayer: __esri.GraphicsLayer;
    hasPreviousFeature: boolean;
    hasNextFeature: boolean;
    private tempFeatures: IFeature[];

    areChildTablesDisplayed: boolean = false;
    parentIdToChildTableDict = new Map<number, ILayerTableChild[]>();
    baseParentIdDict = new Map<number, number>();
    metaTableColumnsDict = new Map<number, IMetaTableColumn[]>();
    currentChildTable: any;
    currentChildTableData: any;
    currentChildTableDataIndex: number;
    tableIdToTableDictionary: { [tableId: number]: any[] } = {};
    docSourceTable: { tableId: number; attributes: any[] };

    private hasPreviousChildTableData: boolean;
    private hasNextChildTableData: boolean;

    get columnSettingsStorageKey() {
        let storageKey: string;
        if (this.currentFeature && this.currentFeature.layer) {
            const mapcoreProps = this.layerService.getSourceMapcoreProps(this.currentFeature.layer);
            const projectIdClause = this.toSqlStringService.getPetlProjectIdClauseIfPresent(mapcoreProps.parentId, mapcoreProps.query, mapcoreProps.isPETL);
            if (projectIdClause) {
                // use a different storage key for PETL layers
                storageKey = keys.storage.columnSettingsStorage.replace('{layer_id}', 'petl-' + projectIdClause.value);
            } else if (!mapcoreProps.isExternal) {
                storageKey = keys.storage.columnSettingsStorage.replace('{layer_id}', 'table-' + this.getTableId(mapcoreProps).toString());
            } else {
                storageKey = keys.storage.columnSettingsStorage.replace('{layer_id}', 'ex-' + mapcoreProps.hashedUrl);
            }
        }
        return storageKey;
    } //**TODO Handle non-mapcore layers**

    private getTableId(mapcoreProps: IMapcoreProps) {
        if (mapcoreProps && mapcoreProps.tables && mapcoreProps.tables[0].id) {
            return mapcoreProps.tables[0].id;
        }
        return 0;
    }

    localStorageService: StorageService;
    updateStorage: boolean = true;

    get showAll() { return this._showAll; }
    set showAll(val) {
        const change = this._showAll !== val;
        this._showAll = val;
        if (this.currentFeature != undefined) {
            (async () => {
                if (this.updateStorage) {
                    if (await this.localStorageService.hasKey(this.columnSettingsStorageKey)) {
                        await this.localStorageService.setPropertyOfObject(this.columnSettingsStorageKey, [], 'showAll', this._showAll);
                    } else {
                        this.createColumnSettingsStorage();
                    }
                }

                this.cdr.detectChanges();
                if (change) {
                    await this.updateCurrentFeature();
                }
            })();
        }
    }
    private _showAll = false;

    @Output() readonly bufferFeature: EventEmitter<any> = new EventEmitter();
    @Output() public readonly identifiedCRS = new EventEmitter<ICRS>();
    @Output() public readonly featureUpdated = new EventEmitter<IFeature>();
    @Output() public readonly multiselectFeaturesUpdated = new EventEmitter<IFeature[]>();
    @Output() public readonly docSourcesUpdated = new EventEmitter<IDocumentSource>();
    @Output() readonly status: EventEmitter<string> = new EventEmitter();

    @Output() readonly openVideologEvent: EventEmitter<any> = new EventEmitter();
    private _showVideologBtn = false;
    @Input()
    set showVideologBtn(val: boolean) { this._showVideologBtn = val; }
    get showVideologBtn() { return this._showVideologBtn; }

    @Output() readonly openStreetViewEvent: EventEmitter<any> = new EventEmitter();
    private _showStreetViewBtn = false;
    @Input()
    set showStreetViewBtn(val: boolean) { this._showStreetViewBtn = val; }
    get showStreetViewBtn() { return this._showStreetViewBtn; }

    @Input()
    @InputConverter(BooleanConverter)
    findNearestSegment = false;

    public currentFeatureHasCRS: boolean = false;

    // TODO: There is a new process for finding these out for a given layer. We should use that. Li uses for filter.
    private readonly countyDBNames = ['CTY_CODE'];
    private readonly routeDBNames = ['ST_RT_NO'];
    private readonly segmentDBNames = ['SEG_NO', 'SEG_BGN'];
    private readonly offsetDBNames = ['OFFSET', 'OFFSET_BGN'];

    @ViewChild('reportLinks') readonly reportLinks: ElementRef<any>;
    @ViewChild('docSource') readonly docSource: ElementRef<any>;

    @Input()
    set sortType(val: ColumnSortOrder) {
        let change = this._sortType !== val;
        this._sortType = val;
        if (this.currentFeature != undefined) {
            (async () => {
                if (this.updateStorage) {
                    if (await this.localStorageService.hasKey(this.columnSettingsStorageKey)) {
                        await this.localStorageService.setPropertyOfObject(this.columnSettingsStorageKey, [], 'sortBy', this._sortType);
                    } else {
                        await this.createColumnSettingsStorage();
                    }
                }

                if (this._sortType === 'custom') {
                    change = await this.isCustomSortChanged();
                }
                if (change) {
                    await this.sortFeature();
                }
            })();
        }
    }
    get sortType() { return this._sortType; }
    private _sortType: ColumnSortOrder = 'database';
    sortTypeOptions: ColumnSortOrder[] = ['alphabetical', 'database'];
    sortTypeDisplayOptions: {} = { alphabetical: 'Alphabetical', database: 'Database' };
    enableCustom: boolean = false;

    // Indicates whether the identify dialog is in multiple selection mode.
    private isMultiselectMode: boolean;

    // Indicates whether the click that initiated the identify task was a Ctrl+Click.
    private isMultiselectClick: boolean;

    constructor(_vcr: ViewContainerRef,
        private readonly layerService: LayerService,
        private readonly documentService: DocumentService,
        private readonly toSqlStringService: ToSqlStringService,
        private readonly cdr: ChangeDetectorRef,
        private readonly dataDictionaryApi: DataDictionaryApi,
        private readonly gisToolsApi: GisToolsApi,
        @Inject('LoggingService') public LoggingService: ILoggingService,
        @Inject('UserSettingsService') public userSettingsService: IUserSettingsService,
        @Inject('authenticationService') authService: IAuthenticationService) {
        super(_vcr);
        this.localStorageService = new StorageService();
        this.registerEvent('featureUpdated', this.featureUpdated);
        this.registerEvent('multiselectFeaturesUpdated', this.multiselectFeaturesUpdated);
        this.registerEvent('docSourcesUpdated', this.docSourcesUpdated);
        this.localStorageService = new StorageService({ userSettingsService, authService });
        authService.user.subscribe(user => {
            this.loggedIn = !!user;
        });
        authService.permissions.subscribe(permissions => {
            this.isCwopaUser = permissions && permissions.includes('View CWOPA Data');
        });
    }

    async ngOnInit() {
        try {
            this.mapView = await this.setupMapViewRef(this.mapView);
            this.activate();
            [Graphic] = await Promise.all([ESRI.Graphic]);
        } catch (error) {
            this.readyProblem(error);
        } finally {
            this.isReady();
        }
    }

    async ngAfterViewInit() {
        if (this.docSource) {
            this.docSource.nativeElement.addEventListener('docSourcesUpdated', docSources => {
                this.docSourcesUpdated.emit(docSources);
            });
        }
    }

    private async createColumnSettingsStorage() {
        const columnStorageSettings: IColumnSettingsStorage = { showAll: this.showAll, sortBy: this.sortType, columnSettings: [], enableCustom: this.enableCustom };
        await this.localStorageService.set(this.columnSettingsStorageKey, columnStorageSettings);
    }

    @Input() public activate = () => {
        if (!this.identifyHandler) {
            this.identifyHandler = this.mapView.on('click', (event: __esri.ViewClickEvent) => {
                this.isMultiselectClick = event.native.ctrlKey;

                // Cache this before it gets reset in case it's needed for handling multiple selection
                this.selectedFeatures = this.features;

                if (this.isMultiselectClick && !this.isMultiselectMode && this.currentFeature != null) {
                    // Switching from single select to multiselect
                    this.clearIdentifyHighlightLayer(false);
                    this.isMultiselectMode = true;
                } else if (!this.isMultiselectClick && this.isMultiselectMode) {
                    // Switching from multiselect to single select
                    this.clearIdentifyHighlightLayer(true);
                    this.isMultiselectMode = false;
                }

                this.featureUpdated.emit(null);
                this.docSourcesUpdated.emit(null);
                this.cdr.detectChanges();
                if (this.enableNearbyMapMode) {
                    const pointOfInterest = new Graphic({
                        geometry: {
                            type: 'point',
                            spatialReference: 3857,
                            latitude: event.mapPoint.latitude,
                            longitude: event.mapPoint.longitude
                        } as __esri.GeometryProperties,
                        symbol: {
                            type: 'simple-marker'
                        } as __esri.SymbolProperties
                    });
                    this.currentFeature = pointOfInterest as IFeature;
                    this.showNearbyAssets = true;
                    this.showIdentify = true;
                } else {
                    this.loading = true;
                    this.status.emit('loading');
                    this.showIdentify = false;
                    this.showNearbyAssets = false;
                    this.executeIdentifyTask(event.mapPoint);
                }
                this.cdr.detectChanges();
            });
        }
    }

    @Input() public deactivate = () => {
        if (this.identifyHandler) {
            this.identifyHandler.remove();
            this.identifyHandler = null;
        }
    }

    @Input() public refresh = () => {
        this.executeIdentifyTask(this.lastExecutePoint, this.lastCurrentFeature);
    }

    public openNearbyAssets() {
        this.showNearbyAssets = true;
    }

    private lastExecutePoint: __esri.Point;
    private lastCurrentFeature: { lastFeaturesLength: number; lastFeatureIndex: number };
    async executeIdentifyTask(mapPoint: __esri.Point, selectedFeatureOverride?: { lastFeaturesLength: number; lastFeatureIndex: number }) {
        const lastFeature = this.currentFeature;
        this.currentFeature = null;
        this.resetIdentify();
        await this.updateShowAllValue();
        await this.updateSortByValue();
        this.lastExecutePoint = mapPoint;
        try {
            const layerOrder = this.mapView.map.allLayers.toArray().map(layer => layer.id);

            const allIndentifyResults = await Promise.all([
                this.identifyGraphicsLayers(this.mapView.toScreen(mapPoint)),
                this.layerService.identifyMapImagelayers(mapPoint, true, true),
                this.identifyUrlFeatureLayers(mapPoint)
            ]);
            const allFeatureResults = allIndentifyResults.map(r => r.features).flat().sort((a, b) => {
                const aIndex = layerOrder.findIndex(id => id === a.layer.id);
                const bIndex = layerOrder.findIndex(id => id === b.layer.id);
                if (aIndex < bIndex) {
                    return 1;
                } else if (aIndex > bIndex) {
                    return -1;
                }
                return 0;
            });
            const fieldDictionary = allIndentifyResults.reduce((prev, curr) => ({ ...prev, ...curr.fields }), {} as IFieldsDictionary);
            const metaTableIds = new Set<number>();
            for (const featureResult of allFeatureResults) {
                if (featureResult.layer) {
                    const mapcoreProps = this.layerService.getSourceMapcoreProps(featureResult.layer);
                    if (mapcoreProps && mapcoreProps.columns && !mapcoreProps.columns.some(c => !!c.domainValues) && mapcoreProps.metaTableId) {
                        metaTableIds.add(mapcoreProps.metaTableId);
                    }
                }
            }
            await Promise.all(Array.from(metaTableIds).map(async (metaTableId) => {
                if (!this.metaTableColumnsDict.has(metaTableId)) {
                    const dictionary = await this.dataDictionaryApi.getColumnsWithDomainValuesByMetaTableId(metaTableId);
                    if (dictionary && dictionary.length) {
                        this.metaTableColumnsDict.set(metaTableId, dictionary);
                    }
                }
            }));
            const results = allFeatureResults.map(result => {
                let columns: ILayerColumn[] = [];
                if (result.layer) {
                    const mapcoreProps = this.layerService.getSourceMapcoreProps(result.layer);
                    if (mapcoreProps && mapcoreProps.columns) {
                        if (mapcoreProps.metaTableId) {
                            const dictionary = this.metaTableColumnsDict.get(mapcoreProps.metaTableId);
                            if (dictionary) {
                                for (const column of dictionary) {
                                    const matchingColumn = mapcoreProps.columns.find(c => c.name.toLowerCase().trim() === column.columnName.toLowerCase().trim());
                                    if (matchingColumn) {
                                        matchingColumn.domainValues = column.domainValues;
                                    }
                                }
                            }
                        }
                        columns = mapcoreProps.columns;
                    }
                }
                const layerId = result.layer.id;
                const fields = fieldDictionary[layerId];
                const retVal: IFeature = {
                    ...result,
                    attributes: Object.entries(result.attributes).reduce((accu, [k, v]) => {
                        let field: __esri.Field;
                        if (fields) {
                            field = fields.find(f => f.name === k);
                        }
                        const attribute = {
                            columnName: k,
                            displayName: k,
                            value: v,
                            dbOrder: accu.count,
                            type: field ? field.type : undefined
                        } as IAttribute;
                        if (columns.length > 0) {
                            const foundColumn = columns.find(x => x.name.toUpperCase() === k);
                            if (foundColumn) {
                                attribute.dbOrder = foundColumn.databaseColumnOrder;
                                attribute.displayName = foundColumn.alias || foundColumn.name;
                                if (foundColumn.domainValues) {
                                    let foundDomainValue;
                                    if (typeof attribute.value === 'number') {
                                        foundDomainValue = foundColumn.domainValues.find(dv => parseFloat(dv.codeValue.toString()) === attribute.value);
                                    } else {
                                        foundDomainValue = foundColumn.domainValues.find(dv => dv.codeValue === attribute.value);
                                    }
                                    if (foundDomainValue) {
                                        attribute.domainValue = `${foundDomainValue.codeValue} - ${foundDomainValue.shortDescription}`;
                                    }
                                }
                            }
                        }
                        accu.count++;
                        accu.attributes.push(attribute);
                        return accu;
                    }, { count: 0, attributes: [] }).attributes
                };
                this.setAttributeIsUrl(retVal);
                return retVal;
            });

            this.clearIdentifyHighlightLayer(!this.isMultiselectClick);
            this.clearIdentifyHighlightLayer(this.isMultiselectClick);

            if (results.length > 0) {
                this.showIdentify = true;
                window.addEventListener('itemInserted', async () => {
                    this.features = this.cloneFeatures(this.tempFeatures);
                    this.updateStorage = false;
                    this.updateSortByValue();
                    await this.updateCurrentFeature();
                    this.updateStorage = true;
                });
                this.features = results;

                if (this.isMultiselectMode) {
                    // Add non-duplicate features to the selected features
                    const sortAttributes = (a, b) => a.columnName.localeCompare(b.columnName);
                    const collectAttributes = f => JSON.stringify(f.attributes.sort(sortAttributes));
                    const collectedAttributes = new Set(this.selectedFeatures.map(collectAttributes));
                    const newFeatures = [];

                    for (const feature of results) {
                        const attributes = collectAttributes(feature);

                        if (!collectedAttributes.has(attributes)) {
                            newFeatures.push(feature);
                        }
                    }

                    this.features = Array.from(this.selectedFeatures);

                    if (newFeatures.length > 0) {
                        // Add the new features and highlight the top one
                        this.features = this.features.concat(newFeatures);
                        this.currentFeatureIndex = this.features.length - newFeatures.length;
                    } else {
                        // No new features were selected, just highlight the top one that was clicked
                        const firstFeature = collectAttributes(results[0]);
                        this.currentFeatureIndex = this.features.findIndex(f => collectAttributes(f) === firstFeature);
                    }
                } else {
                    this.features = results;
                    this.currentFeatureIndex = 0;
                }

                this.tempFeatures = this.cloneFeatures(this.features);
                if (selectedFeatureOverride) {
                    this.currentFeatureIndex = selectedFeatureOverride.lastFeatureIndex;
                    if (this.currentFeatureIndex >= this.features.length) {
                        this.currentFeatureIndex = this.features.length - 1;
                    }
                }
                await this.updateCurrentFeature();
                if (this.currentFeature === lastFeature && !this.isMultiselectClick) {
                    this.showNearbyAssets = true;
                }
            } else {
                if (!this.showNearbyAssets) {
                    this.showIdentify = false;
                    this.featureUpdated.emit(null);
                    this.docSourcesUpdated.emit(null);
                }
            }
        } catch (error) {
            this.LoggingService.Log(error, LogLevel.Error);
        } finally {
            this.loading = false;
            this.status.emit('loaded');
            this.cdr.detectChanges();
        }
    }

    private cloneFeatures(results: IFeature[]) {
        if (results && results.length) {
            const featureClones: any = results.filter((result) => {
                const featureInfo = result as any;
                return featureInfo;
            }).map((result) => {
                const featureClone: IFeature = {
                    geometry: result.geometry,
                    childTables: result.childTables,
                    isAggregate: result.isAggregate,
                    layer: result.layer,
                    layerName: result.layerName || result.layer.title,
                    popupTemplate: result.popupTemplate,
                    sourceLayer: result.sourceLayer,
                    symbol: result.symbol,
                    attributes: result.attributes,
                    visible: result.visible
                };
                return featureClone;
            }).filter(x => x !== undefined);
            return featureClones;
        } else {
            return [];
        }
    }

    private async identifyGraphicsLayers(screenPoint: __esri.MapViewScreenPoint): Promise<IIdentifyResult> {
        const response = await this.mapView.hitTest(screenPoint);
        const retVal: IIdentifyResult = { features: [], fields: {} };
        if (response.results.length) {
            retVal.features = response.results.filter((result) => {
                const layer = result.graphic.layer as any;
                return layer
                    && (layer.type === 'graphics' || layer.source)
                    && this.layerService.layerIdentifyEnabled(layer)
                    && layer.visible
                    && layer.type !== 'feature';
            }).map((result) => {
                const graphic = result.graphic.clone();
                const graphicClone: IFeatureResult = {
                    attributes: { ...graphic.attributes },
                    geometry: graphic.geometry.clone(),
                    layer: graphic.layer,
                    layerName: graphic.layer.title
                };
                if (!graphicClone.attributes) {
                    graphic.attributes = {};
                }
                switch (graphicClone.geometry.type) {
                    case 'point':
                        graphicClone.attributes.LATITUDE = (graphic.geometry as any).latitude;
                        graphicClone.attributes.LONGITUDE = (graphic.geometry as any).longitude;
                        break;
                    case 'polyline':
                        //TODO
                        break;
                    default:
                        break;
                }
                return graphicClone;
            }).filter(x => x !== undefined);
        }
        return retVal;
    }

    private async identifyUrlFeatureLayers(mapPoint: __esri.Point): Promise<IIdentifyResult> {
        const query = {
            geometry: mapPoint,
            spatialRelationship: 'intersects',
            returnGeometry: true,
            returnQueryGeometry: true,
            outFields: ['*']
        } as __esri.supportQueryProperties;
        this.layerService.setTolerance(query);
        const featureLayers = this.mapView.map.allLayers.filter((layer: __esri.FeatureLayer) =>
            layer.type === 'feature'
            && this.layerService.layerIdentifyEnabled(layer)
            && layer.visible) as __esri.Collection<__esri.FeatureLayer>;
        const responses = await Promise.all(featureLayers.map((layer: IMapcoreFeatureLayer) => {
            const layerQuery = { ...query } as __esri.supportQueryProperties;
            layerQuery.where = this.toSqlStringService.getSql(layer.mapcoreProps.query);
            return layer.queryFeatures(layerQuery);
        }).toArray());
        return responses.reduce<IIdentifyResult>((retVal, response) => {
            retVal.features.push(...response.features.map(feature => ({
                attributes: { ...feature.attributes },
                geometry: feature.geometry ? feature.geometry.clone() : null,
                layer: feature.layer,
                layerName: feature.layer.title
            } as IFeatureResult)));
            if (response.fields && response.features && response.features.length) {
                retVal.fields[response.features[0].layer.id] = response.fields;
            }
            return retVal;
        }, { features: [], fields: {} } as IIdentifyResult);
    }

    @Input() previousFeature = async () => {
        if (this.hasPreviousFeature) {
            this.currentChildTable = null;
            this.currentChildTableData = null;
            this.currentFeatureIndex--;
            await this.updateCurrentFeature();
        }
    }

    @Input() nextFeature = async () => {
        if (this.hasNextFeature) {
            this.currentChildTable = null;
            this.currentChildTableData = null;
            this.currentFeatureIndex++;
            await this.updateCurrentFeature();
        }
    }

    @Input() public getHasPreviousFeature = () => {
        return this.hasPreviousFeature;
    }
    @Input() public getHasNextFeature = () => {
        return this.hasNextFeature;
    }
    @Input() public getFeatureCountAndIndex = () => {
        return [this.features.length, this.currentFeatureIndex];
    }
    @Input() public getCurrentFeature = () => {
        return this.currentFeature;
    }

    async toggleCurrentChildTable(table) {
        if (this.currentChildTable === table) {
            this.currentChildTable = null;
            this.currentChildTableData = null;
        } else {
            this.currentChildTable = table;
            this.currentChildTableDataIndex = 0;
            this.updateCurrentChildTableData();
        }
        await this.sortFeature();
    }

    previousChildTableData() {
        if (this.hasPreviousChildTableData) {
            this.currentChildTableDataIndex--;
            this.updateCurrentChildTableData();
        }
    }

    nextChildTableData() {
        if (this.hasNextChildTableData) {
            this.currentChildTableDataIndex++;
            this.updateCurrentChildTableData();
        }
    }

    async updateShowAllValue() {
        if (this.currentFeature != undefined) {
            const settingsStorage = await this.localStorageService.get<IColumnSettingsStorage>(this.columnSettingsStorageKey);
            if (settingsStorage !== null) {
                this.updateStorage = false;
                this.showAll = settingsStorage.showAll;
                this.updateStorage = true;
            }
        }
    }

    async updateSortByValue() {
        if (this.currentFeature != undefined) {
            const settingsStorage = await this.localStorageService.get<IColumnSettingsStorage>(this.columnSettingsStorageKey);
            if (settingsStorage !== null) {
                this.updateStorage = false;
                this.sortType = settingsStorage.sortBy ? settingsStorage.sortBy : 'database';
                this.updateStorage = true;
            }
        }
    }

    updateCurrentChildTableData() {
        this.currentChildTableData = Object.entries(this.currentChildTable.childTableData[this.currentChildTableDataIndex]);
        const attributes = this.currentChildTableData.map(([columnName, value]) => ({ columnName, displayName: columnName, value }));
        this.docSourceTable = { tableId: this.currentChildTable.tableId, attributes };
        const myIndex = this.currentChildTableData.findIndex(x => x[0] === 'DATA');
        if (myIndex !== -1) {
            this.currentChildTableData.splice(myIndex, 1);
        }
        this.hasPreviousChildTableData = this.currentChildTableDataIndex > 0;
        this.hasNextChildTableData = this.currentChildTableDataIndex < this.currentChildTable.childTableData.length - 1;
    }

    async updateCurrentFeature() {
        if (!this.features || this.currentFeatureIndex < 0 || this.features.length < this.currentFeatureIndex) {
            return;
        }
        this.hasPreviousFeature = this.currentFeatureIndex > 0;
        this.hasNextFeature = this.currentFeatureIndex < this.features.length - 1;
        this.areChildTablesDisplayed = false;
        this.currentChildTable = null;
        this.showAdditionalInformation = true;
        this.currentFeature = this.features[this.currentFeatureIndex];
        this.lastCurrentFeature = { lastFeatureIndex: this.currentFeatureIndex, lastFeaturesLength: this.features.length };
        const settingsStorage = await this.localStorageService.get<IColumnSettingsStorage>(this.columnSettingsStorageKey);
        if (settingsStorage && settingsStorage.enableCustom) {
            this.enableCustom = true;
        } else {
            this.enableCustom = false;
        }

        const crs = await this.getCRSValuesFromCurrentFeature();
        this.currentFeatureHasCRS = crs !== undefined;
        this.featureUpdated.emit(this.currentFeature);
        this.identifiedCRS.emit(crs);

        if (this.isMultiselectClick) {
            this.multiselectFeaturesUpdated.emit(this.features);
        }

        if (this.currentFeature && this.currentFeature.layer !== undefined) {
            if (this.enableCustom) {
                this.sortTypeDisplayOptions = { alphabetical: 'Alphabetical', database: 'Database', custom: 'Custom' };
                this.sortTypeOptions = ['alphabetical', 'database', 'custom'];
            }

            await this.updateShowAllValue();
            await this.updateSortByValue();
        } else {
            //remove custom sort for non-system layers
            this.sortTypeDisplayOptions = { alphabetical: 'Alphabetical', database: 'Database' };
            this.sortTypeOptions = ['alphabetical', 'database'];
        }
        await this.updateFieldVisibility();
        await this.sortFeature();
        this.highlightIdentifyGraphics();
        this.setMapRmsObjectFromSelectedFeature();
        this.cdr.detectChanges();
    }

    async updateFieldVisibility() {
        if (this.showAll) {
            for (const attr of this.currentFeature.attributes.filter(attribute => attribute.hide)) {
                attr.hide = false;
            }
            //force update shown grid, changes won't display otherwise
            this.cdr.detectChanges();
            return;
        }
        //retrieve the visibility object from Local Storage
        const settingsStorage = await this.localStorageService.get<IColumnSettingsStorage>(this.columnSettingsStorageKey);
        let shownColumns: ISmallColumnSetting[];
        if (settingsStorage) {
            shownColumns = settingsStorage.columnSettings;
        }
        if (!shownColumns || !shownColumns.length) { return; }
        //uppercase all column names in shownColumns to match how field represents the column names
        for (const c of shownColumns) {
            c.name = c.name.toUpperCase();
        }
        //filter feature attributes to only include attributes that match a visible column OR
        //are a reserved column
        for (const attr of this.currentFeature.attributes) {
            if (shownColumns.findIndex(shownColumn => shownColumn.visible && shownColumn.name === attr.columnName) === -1) {
                attr.hide = true;
            } else if (attr.hide) {
                attr.hide = false;
            }
        }
        //force update shown grid, changes won't display otherwise
        this.cdr.detectChanges();
    }

    setAttributeIsUrl(feature: IFeature) {
        if (feature && feature.attributes) {
            for (const attribute of feature.attributes) {
                if (attribute && attribute.value && this.isUrl(attribute.value) && !(attribute.type === 'download-url' || attribute.type === 'open-url')) {
                    attribute.type = 'open-url';
                }
            }
        }
    }

    setMapRmsObjectFromSelectedFeature() {
        if (this.currentFeature) {
            let rms: IRMS = null;
            const mapcoreProps = this.layerService.getSourceMapcoreProps(this.currentFeature.layer);
            if (mapcoreProps && mapcoreProps.columns) {
                const columns = mapcoreProps.columns;
                const countyColumn = this.getMatchingColumn(columns, 'countyCode');
                const stateRouteColumn = this.getMatchingColumn(columns, 'routeNumber');
                const segBgnColumn = this.getMatchingColumn(columns, 'segBgn');
                let params = '';
                if (countyColumn) {
                    const attributes = this.currentFeature.attributes;
                    rms = { countyCode: this.getMathingFeatureValue(attributes, countyColumn.name) };
                    params += 'cn=' + rms.countyCode;
                    if (stateRouteColumn) {
                        rms.stateRoute = this.getMathingFeatureValue(attributes, stateRouteColumn.name);
                        params += '&rt=' + rms.stateRoute;
                    }
                    if (segBgnColumn) {
                        rms.segmentBegin = this.getMathingFeatureValue(attributes, segBgnColumn.name);
                        params += '&sg=' + rms.segmentBegin;
                    }
                } else {
                    rms = null;
                }
                if (!(<any>this.mapView.map).mapcoreProps) {
                    (<any>this.mapView.map).mapcoreProps = {};
                }
                (<any>this.mapView.map).mapcoreProps.rms = rms;
                if (rms) {
                    (<any>this.mapView.map).mapcoreProps.rms.params = params;
                }
            }
        }
    }

    getMatchingColumn(columns: any[], name: string) {
        const foundArray = columns.filter(x => x.columnIdentifier && x.columnIdentifier.name === name);
        if (foundArray.length > 0) {
            return foundArray[0];
        }
        return null;
    }

    getMathingFeatureValue(attriubutes: any[], columnName: string) {
        const foundArray = attriubutes.filter(x => x.columnName === columnName);
        if (foundArray.length > 0) {
            return foundArray[0].value;
        }
        return null;
    }

    private async clearIdentifyHighlightLayer(multiselect: boolean) {
        if (multiselect) {
            if (this.multiIdentifyHighlightLayer) {
                this.addLayerIfMissing(this.multiIdentifyHighlightLayer);
                this.multiIdentifyHighlightLayer.removeAll();
            }

            if (this.multiIdentifyHighlightLayer == undefined) {
                this.multiIdentifyHighlightLayer = await this.layerService.addGraphicsLayer({
                    title: 'Multiselect identify',
                    listMode: 'hide'
                }, {
                    hideInLegend: true,
                    identifyEnabled: false
                });
            }
        } else {
            if (this.identifyHighlightLayer) {
                this.addLayerIfMissing(this.identifyHighlightLayer);
                this.identifyHighlightLayer.removeAll();
            }

            if (this.identifyHighlightLayer == undefined) {
                this.identifyHighlightLayer = await this.layerService.addGraphicsLayer({
                    title: 'Identify',
                    listMode: 'hide'
                }, {
                    hideInLegend: true,
                    identifyEnabled: false
                });
            }
        }
    }

    private addLayerIfMissing(layer: __esri.Layer) {
        if (!this.mapView.map.findLayerById(layer.id)) {
            this.mapView.map.add(layer);
        }
    }

    private highlightIdentifyGraphics() {
        this.clearIdentifyHighlightLayer(this.isMultiselectMode).then(() => {
            if (this.isMultiselectMode) {
                this.layerService.moveLayerToTheTop(this.multiIdentifyHighlightLayer);

                for (const feature of this.features.slice().reverse()) {
                    if (feature.geometry.type !== 'point' || feature === this.currentFeature) {
                        continue;
                    }

                    this.highlightSingleIdentifyGraphics(this.multiIdentifyHighlightLayer, feature, this.multiselectHighlightColor);
                }

                if (this.currentFeature) {
                    this.highlightSingleIdentifyGraphics(this.multiIdentifyHighlightLayer, this.currentFeature, this.highlightColor);
                }
            } else {
                this.layerService.moveLayerToTheTop(this.identifyHighlightLayer);
                this.highlightSingleIdentifyGraphics(this.identifyHighlightLayer, this.currentFeature, this.highlightColor);
            }
        });
    }

    private highlightSingleIdentifyGraphics(layer: __esri.GraphicsLayer, feature: IFeature, highlightColor: string) {
        if (feature && feature.geometry) {
            const highlightGeometry = feature.geometry;
            let highlightSymbol;
            if (feature.symbol) {
                highlightSymbol = feature.symbol;
            } else {
                switch (feature.geometry.type) {
                    case 'point': highlightSymbol = { type: 'simple-marker', size: this.highlightMarkerSize, outline: { color: this.highlightColor } }; break;
                    case 'polyline': highlightSymbol = { type: 'simple-line', width: this.highlightLineWidth }; break;
                    case 'polygon': highlightSymbol = { type: 'simple-fill' }; break;
                    default: highlightSymbol = { type: 'simple-marker', size: this.highlightMarkerSize }; break;
                }
            }

            highlightSymbol.color = highlightColor;

            layer.add(new Graphic({
                geometry: highlightGeometry,
                symbol: highlightSymbol
            }));
        }
    }

    async sortFeature() {
        if (!this.currentFeature) {
            return;
        }
        switch (this.sortType) {
            case 'alphabetical':
                this.currentFeature.attributes.sort((a, b) => {
                    if (a.displayName < b.displayName) {
                        return -1;
                    }
                    if (a.displayName > b.displayName) {
                        return 1;
                    }
                    return 0;
                });
                break;
            case 'database':
                this.currentFeature.attributes.sort((a, b) => {
                    if (a.dbOrder < b.dbOrder) {
                        return -1;
                    }
                    if (a.dbOrder > b.dbOrder) {
                        return 1;
                    }
                    return 0;
                });
                break;
            case 'custom':
                const settingsStorage = await this.localStorageService.get<IColumnSettingsStorage>(this.columnSettingsStorageKey);
                const shownColumns = settingsStorage.columnSettings;
                this.currentFeature.attributes.sort(
                    (a, b): number => (
                        shownColumns.findIndex(
                            c => c.name.toUpperCase() === a.columnName.toUpperCase()
                        ) -
                        shownColumns.findIndex(
                            c => c.name.toUpperCase() === b.columnName.toUpperCase()
                        )
                    )
                );
                break;
            default:
                break;
        }
    }

    hideIdentify() {
        this.showIdentify = false;
        this.isMultiselectMode = false;
        window.removeEventListener('itemInserted', () => { });
        this.featureUpdated.emit(null);
        this.docSourcesUpdated.emit(null);
        this.resetIdentify();
        this.clearIdentifyHighlightLayer(true);
        this.clearIdentifyHighlightLayer(false);
        this.multiIdentifyHighlightLayer.removeAll();
        this.cdr.detectChanges();
    }

    public async showChildTables(currentFeature: IFeature) {
        try {
            this.areChildTablesDisplayed = !this.areChildTablesDisplayed;
            const childTables = currentFeature.childTables;
            if (this.areChildTablesDisplayed && childTables) {
                if (childTables.filter(x => x.childTableData).length === 0) {
                    this.loading = true;
                    const parentQuery = currentFeature.sourceLayer.parent.mapcoreProps.query;
                    const childTablePromises = childTables.map(async (childTable) => {
                        childTable.childTableData = [];
                        // Get columns
                        if (!this.tableIdToTableDictionary[childTable.tableId]) {
                            const tablesResult = await this.layerService.getLayerChildTable(childTable.layerId, childTable.tableId, 'id name columns{name dataType databaseColumnOrder}');
                            if (tablesResult.length > 0) {
                                this.tableIdToTableDictionary[childTable.tableId] = tablesResult[0];
                            }
                        }
                        if (this.tableIdToTableDictionary[childTable.tableId]) {
                            childTable.table = this.tableIdToTableDictionary[childTable.tableId];
                        }
                        // Get data
                        if (childTable.table) {
                            const childColumnKeys = childTable.childParentKeyColumnName.replaceAll(' ', '').split('||');
                            const parentKeys = childTable.parentPrimaryKeyColumnName.split('||');
                            const paddingField = parentKeys.filter(x => x.toUpperCase().includes('PAD'))[0];
                            const objIdFields = parentKeys.filter(x => (!x.includes('\'')));
                            let paddingObjectIdField = '';
                            if (paddingField) {
                                const index: number = paddingField.indexOf('(');
                                paddingObjectIdField = paddingField.substr(index + 1, paddingField.length - paddingField.indexOf(','));
                                objIdFields.push(paddingObjectIdField);
                            }
                            const attributes = currentFeature.attributes.filter(x => objIdFields.map(y => y.trim()).indexOf(x.columnName) > -1);
                            for (const attribute of attributes) {
                                if (paddingField) {
                                    if (paddingObjectIdField) {
                                        parentKeys[parentKeys.indexOf(paddingField)] = (`${paddingField.replace(paddingObjectIdField, attribute.value.toString())}`);
                                    }
                                } else {
                                    parentKeys[parentKeys.map(y => y.trim()).indexOf(attribute.columnName)] = (`${attribute.value}`);
                                }
                            }
                            const columns = childTable.table.childTables[0].columns;
                            columns.sort((a, b) => a.databaseColumnOrder - b.databaseColumnOrder);
                            const fieldNames = columns.map(col => col.name);
                            if (parentKeys) {
                                const queryFilter = this.buildQueryFilter(parentKeys, childColumnKeys);
                                if (parentQuery.childTableRecord && childTable.tableId === parentQuery.childTableRecord.id) {
                                    queryFilter.subqueries.push(parentQuery.childTableQuery);
                                }
                                const childTableDataValue = await this.layerService.getLayerChildTableWithData(childTable.layerId, childTable.tableId, fieldNames, queryFilter);
                                const fieldValues = childTableDataValue[0].childTables[0].data;
                                if (fieldValues.length) {
                                    childTable.childTableData = fieldValues.map(values => {
                                        const keyValues = {};
                                        for (let i = 0; i < fieldNames.length; i++) {
                                            keyValues[fieldNames[i]] = values[i];
                                        }
                                        return keyValues;
                                    });
                                }
                                this.combineChildTableData(childTable, childColumnKeys);
                                if (childTable.orderKeyColumnName && childTable.childTableData.length > 1) {
                                    childTable.childTableData.sort((a, b) => {
                                        const sortValueA = b[childTable.orderKeyColumnName] as string | number;
                                        const sortValueB = a[childTable.orderKeyColumnName] as string | number;
                                        if (sortValueA === null || sortValueB === null) {
                                            return sortValueA === null ? (sortValueB === null ? 0 : -1) : 1;
                                        } else if (typeof sortValueA === 'number' || typeof sortValueB === 'number') {
                                            return sortValueA === sortValueB ? 0 : (sortValueA > sortValueB ? 1 : -1);
                                        } else {
                                            return sortValueA.toString().localeCompare(sortValueB.toString());
                                        }
                                    });
                                }
                            }
                        }
                    });
                    await Promise.all(childTablePromises);

                }
            }
            if (!this.areChildTablesDisplayed) {
                this.currentChildTable = null;
                this.currentChildTableData = null;
            }
        } catch (error) {
            this.LoggingService.Log(error, LogLevel.Error);
        } finally {
            this.loading = false;
            this.cdr.detectChanges();
        }
    }

    async combineChildTableData(childTable: any, childColumnKeys: string[]) {
        const sources: any[] = await this.documentService.getTableDocSources(childTable.tableId);
        const docSources = sources && sources.length ? sources[0].docSources : [];
        if (docSources && docSources.length && docSources.length > 0 && docSources[0].fields) {
            const field = JSON.parse(docSources[0].fields);
            if (field.docCombineField) {
                const combinedTableData: any[] = [];
                const childDataKeyValues: Set<string> = new Set<string>();
                childTable.childTableData.forEach(childTableData => {
                    const keyString = childColumnKeys.concat(field.docCombineField).map(key => childTableData[key]).join('|');
                    if (!childDataKeyValues.has(keyString)) {
                        combinedTableData.push(childTableData);
                        childDataKeyValues.add(keyString);
                    }
                });
                if (combinedTableData && combinedTableData.length > 0 && childTable.childTableData && childTable.childTableData.length > 0 && childTable.childTableData.length > combinedTableData.length) {
                    childTable.origialTableData = [...childTable.childTableData];
                    childTable.childTableData = [...combinedTableData];
                    childTable.hideDuplicateRecords = true;
                }
            }
        }
    }

    buildQueryFilter(parentKeys: string[], childKeys: string[]) {
        const query: IQuery = {
            operator: 'AND',
            subqueries: []
        };
        if (parentKeys.length === childKeys.length) {
            for (let i = 0; i < parentKeys.length; i++) {
                query.subqueries.push({
                    field: childKeys[i].toString().trim(),
                    operator: '=',
                    value: parentKeys[i].toString().replaceAll('\'', '').trim()
                });
            }
        }
        return query;
    }

    showDuplicateRecords(childTable) {
        childTable.childTableData = [...childTable.origialTableData];
        childTable.hideDuplicateRecords = false;
        this.currentChildTable = childTable;
        this.currentChildTableDataIndex = 0;
        this.updateCurrentChildTableData();
    }

    resetIdentify() {
        this.loading = false;
        this.features = null;
        this.tempFeatures = null;
        this.currentFeature = null;
        this.currentChildTable = null;
        this.currentChildTableData = null;
        this.areChildTablesDisplayed = false;
        this.showAdditionalInformation = true;
        this.enableCustom = false;
        this.sortType = 'database';
        this.sortTypeOptions = ['alphabetical', 'database'];
        this.sortTypeDisplayOptions = { alphabetical: 'Alphabetical', database: 'Database' };
    }

    openBufferWindow() {
        this.bufferFeature.emit(this.currentFeature.geometry);
    }

    docSourcesUpdatedHandler() {
        if (this.docSource) {
            this.docSourcesUpdated.emit((<any>this.docSource).docSources);
        }
    }

    isUrl(value: string | number) {
        return value
            && typeof value === 'string'
            && value.length <= urlMaxLength
            && /^http[s]?:\/\/(?:[a-zA-Z]|[0-9]|[$-_@.&+]|[!*(), ]|(?:%[0-9a-fA-F][0-9a-fA-F]))+$/.test(value.trim());
    }

    onNearbyActiveChange(active) {
        if (!active && (!this.features || !this.features.length)) {
            this.hideIdentify();
        }
    }

    onNearbyStatusChange(status) {
        this.status.emit(status);
    }

    async openVideolog() {
        if (this.currentFeature) {
            const CRS = await this.getCRSValuesFromCurrentFeature();
            this.openVideologEvent.emit(CRS);
        }
    }

    async openStreetView() {
        if (this.currentFeature) {
            const CRS = await this.getCRSValuesFromCurrentFeature();
            this.openStreetViewEvent.emit(CRS);
        }
    }

    openVideoLogWithCoordinates(coordinates) {
        this.openVideologEvent.emit({ coordinates });
    }

    openStreetViewWithCoordinates(coordinates) {
        this.openStreetViewEvent.emit({ coordinates });
    }

    private async getCRSValuesFromCurrentFeature(): Promise<ICRS> {
        // This function should be enhanced also use the TABLE_COLUMN_IDENTIFIERS process
        let CRS: ICRS;
        if (this.currentFeature && this.currentFeature.attributes) {
            const allNames = [...this.countyDBNames, ...this.routeDBNames, ...this.segmentDBNames, ...this.offsetDBNames];
            const foundAttributes = this.currentFeature.attributes.filter(attr => allNames.includes(attr.columnName.toUpperCase()));
            if (foundAttributes.length) {
                const countyCode = foundAttributes.find(attr => this.countyDBNames.includes(attr.columnName.toUpperCase()));
                const stateRouteNumber = foundAttributes.find(attr => this.routeDBNames.includes(attr.columnName.toUpperCase()));
                const segmentNumber = foundAttributes.find(attr => this.segmentDBNames.includes(attr.columnName.toUpperCase()));
                const offset = foundAttributes.find(attr => this.offsetDBNames.includes(attr.columnName.toUpperCase()));
                CRS = {
                    countyCode: countyCode && countyCode.value ? countyCode.value.toString() : null,
                    stateRouteNumber: stateRouteNumber && stateRouteNumber.value ? stateRouteNumber.value.toString() : null,
                    segmentNumber: segmentNumber && segmentNumber.value ? segmentNumber.value.toString() : null,
                    offset: offset && offset.value ? offset.value.toString() : null
                };
            }

            if (this.findNearestSegment
                && isPointGeometry(this.currentFeature.geometry)
                && (!CRS || !CRS.countyCode || !CRS.stateRouteNumber || !CRS.segmentNumber)) {
                const result = await this.gisToolsApi.getSegmentsClosestToLatLong(this.currentFeature.geometry.latitude, this.currentFeature.geometry.longitude, this.DEFAULT_TOLERANCE);

                if (result && result.length) {
                    CRS = {
                        countyCode: result[0].countyCode,
                        stateRouteNumber: result[0].stateRouteNumber,
                        segmentNumber: result[0].segmentNumber
                    };
                }
            }
        }
        return CRS;
    }

    async isCustomSortChanged() {
        //returns true if current custom sort is different from localstorage custom sort settings (order)
        const settingsStorage = await this.localStorageService.get<IColumnSettingsStorage>(this.columnSettingsStorageKey);
        if (settingsStorage !== null) {
            let count = 0;
            for (const localStorageCol of settingsStorage.columnSettings) {
                const localStorageColName = localStorageCol.name;
                if (this.currentFeature.attributes[count] === undefined) {
                    return true;
                }
                const currentColName = this.currentFeature.attributes[count].columnName;

                if (localStorageColName !== currentColName) {
                    return true;
                }
                count++;
            }
        }
        return false;
    }

    getLayerIdentifier(mapcoreProps: IMapcoreProps): string {
        let layerId = '';

        if (mapcoreProps) {
            if (mapcoreProps.id) {
                layerId = mapcoreProps.id.toString();
            } else if (mapcoreProps.idFromSavedLayer) {
                layerId = mapcoreProps.idFromSavedLayer.toString();
            } else if (mapcoreProps.parentId) {
                layerId = mapcoreProps.parentId.toString();
            }
        }

        return layerId;
    }
}

interface IFieldsDictionary {
    [key: string]: __esri.Field[];
}

interface IIdentifyResult {
    features: IFeatureResult[];
    fields: IFieldsDictionary;
}

interface IFeatureResult extends Partial<__esri.Graphic> {
    layerName?: string;
    childTables?: ILayerTableChild[];
    attributes: { [key: string]: string | number };
    sourceLayer?: IMapcoreSubLayer;
}

interface IFeature extends Partial<__esri.Graphic> {
    layerName?: string;
    childTables?: (ILayerTableChild & { childTableData?: any[]; origialTableData?: any[]; hideDuplicateRecords?: boolean; table?: any })[];
    attributes: IAttribute[];
    sourceLayer?: IMapcoreSubLayer;
}

interface ICRS {
    countyCode: string;
    stateRouteNumber: string;
    segmentNumber: string;
    offset?: string;
}
